import { animated } from "@react-spring/web";
import {
  type ComponentProps,
  type ReactElement,
  type SyntheticEvent,
  cloneElement,
  useEffect,
  useState,
} from "react";
import styled from "styled-components";
import { Backdrop } from "../Backdrop/Backdrop";
import { Portal } from "../Portal/Portal";
import { Fade } from "../Transition/Fade";

// The order prop sets the visibility priority of the modal on the page.
// Higher values appear in front of lower values. The default value is 1
type Props = {
  open: boolean;
  onBackdropClicked?: ComponentProps<typeof Backdrop>["onClick"];
  children: ReactElement<{ onEnter(): void; onExited(): void }>;
  order?: number;
  displayAtBase?: boolean;
  disableBackgroundScroll?: boolean;
};

export function Modal(props: Props) {
  const [exited, setExited] = useState(true);

  const {
    onBackdropClicked,
    open,
    displayAtBase,
    order,
    children,
    disableBackgroundScroll = true,
  } = props;

  useEffect(() => {
    if (document.documentElement && document.body) {
      if (open && disableBackgroundScroll) {
        document.documentElement.style.overflow = "hidden";
      }
      if (open) {
        document.body.style.touchAction = "none";
      } else {
        document.documentElement.style.overflow = "";
        document.body.style.touchAction = "";
      }
      // Called on unmount to cleanup any styles.
      return () => {
        document.documentElement.style.overflow = "";
        document.body.style.touchAction = "";
      };
    }
  }, [open, disableBackgroundScroll]);

  const handleEnter = () => {
    setExited(false);
  };

  const handleExit = () => {
    setExited(true);
  };

  if (!open && exited) {
    return null;
  }

  return (
    <Portal>
      <Root
        $open={open}
        $order={order ?? 1}
        $displayAtBase={displayAtBase}
        onClick={(e: SyntheticEvent) => e.stopPropagation()}
      >
        {!displayAtBase && (
          <Fade open={open} onEnter={handleEnter} onExited={handleExit}>
            <AnimatedBackdrop open={open} onPointerDown={onBackdropClicked} />
          </Fade>
        )}
        {cloneElement(children, {
          onEnter: handleEnter,
          onExited: handleExit,
        })}
      </Root>
    </Portal>
  );
}

const Root = styled.div<{
  $open: boolean;
  $order: number;
  $displayAtBase?: boolean;
}>`
  position: fixed;
  right: 0;
  bottom: 0;
  top: ${({ $displayAtBase }) => ($displayAtBase ? "auto" : "0")};
  left: 0;
  z-index: ${({ $order }) => 10000 + $order};
  pointer-events: ${({ $open }) => ($open ? "initial" : "none")};
`;

const AnimatedBackdrop = animated(Backdrop);
